/**
 * Month-grid statistics for the doctor's calendar.
 *
 * Extracted from `renderGlobalCalendar`, which recomputed all of this inside a
 * 900-line string template and then patched the DOM afterwards in a
 * `setTimeout` to add the "fully booked" glow. Here it is one pure pass over
 * the month, which means it can be tested without rendering anything.
 */

import { clinicSlotsForDate, isClinicWorkingDay } from './slots';
import { bestDayDiscount, getActiveOffersForDate } from './offers';

/** Bookings that occupy a slot — cancelled and waitlisted do not. */
const OCCUPYING = (booking) =>
  booking.status !== 'cancelled' && booking.status !== 'waitlist';

/**
 * Everything one day cell needs, computed in a single pass over the month.
 *
 * @returns {Map<string, object>} date key -> { totalSlots, bookedSlots, … }
 */
export function buildMonthStats({ year, month, clinics = [], bookings = [], offers = [], extraHours = [] }) {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const stats = new Map();
  const pad = (n) => String(n).padStart(2, '0');

  // Only this doctor's own clinics count towards a day. Built once, not per day.
  const clinicIds = new Set(clinics.map((c) => String(c.id)));

  // Index bookings by date once — the legacy code filtered the whole array
  // inside three nested loops per day, which is O(days × clinics × bookings).
  const byDate = new Map();
  for (const booking of bookings) {
    if (!byDate.has(booking.date)) byDate.set(booking.date, []);
    byDate.get(booking.date).push(booking);
  }

  for (let day = 1; day <= daysInMonth; day += 1) {
    const dateKey = `${year}-${pad(month + 1)}-${pad(day)}`;
    const dayBookings = byDate.get(dateKey) || [];

    const workingClinics = clinics.filter((clinic) => isClinicWorkingDay(clinic, dateKey));

    // A clinic that takes two patients per slot has twice the capacity, which
    // is what decides whether the day is full — the legacy grid counted slots
    // only and showed such a day as booked out at half capacity.
    let totalSlots = 0;
    for (const clinic of workingClinics) {
      const capacity = Number(clinic.maxBookingsPerSlot) || 1;
      totalSlots += clinicSlotsForDate(clinic, dateKey).length * capacity;
    }

    // Extra-hours blocks add slots on top of the regular grid. A time already
    // inside a clinic's own window is not an extra slot — counting it twice
    // would inflate "متاح" on the cell against what the booking screen offers,
    // which dedupes the same way.
    const dayExtraHours = (extraHours || []).filter((entry) => entry.date === dateKey);
    for (const entry of dayExtraHours) {
      const clinic = workingClinics.find((c) => String(c.id) === String(entry.clinicId));
      const regular = clinic ? new Set(clinicSlotsForDate(clinic, dateKey)) : new Set();
      const capacity = Number(clinic?.maxBookingsPerSlot) || 1;
      const fresh = [...new Set((entry.slots || []).map(String))].filter((t) => !regular.has(t));
      totalSlots += fresh.length * capacity;
    }

    const live = dayBookings.filter(
      (b) => OCCUPYING(b) && clinicIds.has(String(b.clinicId)),
    );

    // All three counts read from the same filtered list, so the pending bubble
    // can never show a booking the day's totals do not include.
    const bookedSlots = live.length;
    const pendingCount = live.filter((b) => b.status === 'pending').length;
    const confirmedCount = live.filter((b) => b.status === 'confirmed').length;

    const offerPercent = bestDayDiscount(offers, dateKey);

    // The patient calendar prints the offer's name on the day, so the winning
    // offer is identified rather than just its percentage.
    const dayOffers = getActiveOffersForDate(offers, dateKey);
    const topOffer = dayOffers.reduce(
      (best, offer) => (!best || offer.percent > best.percent ? offer : best),
      null,
    );
    const extraDiscount = dayExtraHours[0]?.nightOfferDiscount || dayExtraHours[0]?.nightDiscount || 0;

    stats.set(dateKey, {
      dateKey,
      day,
      totalSlots,
      bookedSlots,
      availableSlots: Math.max(0, totalSlots - bookedSlots),
      pendingCount,
      confirmedCount,
      hasOffer: offerPercent > 0,
      offerPercent,
      offerName: topOffer?.name || '',
      hasExtraHours: dayExtraHours.length > 0,
      extraDiscount,
      workingClinics,
      isFull: totalSlots > 0 && bookedSlots >= totalSlots,
    });
  }

  return stats;
}
